<template>
  <div class="space-y-5">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p class="text-xs uppercase tracking-[0.18em] text-cyan-400">Laboratório próprio</p>
        <h1 class="text-2xl font-bold text-white">Paper trading</h1>
        <p class="text-sm text-gray-500 mt-1">Resultados simulados apenas para venues elegíveis e acessíveis do Brasil.</p>
      </div>
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <span class="h-2 w-2 rounded-full" :class="loading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'"></span>
        <span v-if="error" class="text-red-400">{{ error }}</span>
        <span v-else>{{ lastUpdate ? `Atualizado ${formatDate(lastUpdate)}` : 'Aguardando dados' }}</span>
      </div>
    </div>

    <section class="bg-gray-900 border border-emerald-900/50 rounded-2xl p-4">
      <div class="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 class="text-sm font-semibold text-white">Futebol — simulações</h2>
          <p class="text-[11px] text-gray-600">Apostas simuladas com odds reais, apuradas por resultado oficial</p>
        </div>
        <a href="/futebol-lab/" target="_blank" rel="noopener" class="text-[11px] text-emerald-400 hover:text-emerald-300">Abrir laboratório ↗</a>
      </div>
      <div v-if="football.loading" class="text-sm text-gray-600 py-3">Carregando futebol…</div>
      <div v-else-if="football.error" class="text-sm text-red-400 py-3">{{ football.error }}</div>
      <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="bg-gray-800/60 rounded-lg p-3"><p class="text-[10px] text-gray-500">Saldo</p><p class="text-lg font-bold" :class="football.net>=0?'text-emerald-400':'text-red-400'">{{ football.net==null?'—':(football.net>=0?'+':'')+football.net.toFixed(2)+' u' }}</p></div>
        <div class="bg-gray-800/60 rounded-lg p-3"><p class="text-[10px] text-gray-500">Acertos</p><p class="text-lg font-bold text-cyan-400">{{ football.hitRate==null?'—':(football.hitRate*100).toFixed(1)+'%' }}</p></div>
        <div class="bg-gray-800/60 rounded-lg p-3"><p class="text-[10px] text-gray-500">Em aberto</p><p class="text-lg font-bold text-white">{{ football.pending }}</p></div>
        <div class="bg-gray-800/60 rounded-lg p-3"><p class="text-[10px] text-gray-500">Seleções</p><p class="text-lg font-bold text-white">{{ football.total }}</p></div>
      </div>
    </section>

    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div v-for="card in statCards" :key="card.label" class="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p class="text-[11px] text-gray-500 mb-1">{{ card.label }}</p>
        <p class="text-xl font-bold" :class="card.class">{{ card.value }}</p>
      </div>
    </div>

    <section class="grid lg:grid-cols-[1.35fr_0.65fr] gap-4">
      <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4 min-h-[260px]">
        <div class="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 class="text-sm font-semibold text-white">Curva de P&L</h2>
            <p class="text-[11px] text-gray-600">Resultado acumulado das entradas resolvidas</p>
          </div>
          <span class="text-[10px] uppercase tracking-wider text-gray-600">paper</span>
        </div>
        <div v-if="!chartTrades.length" class="h-[190px] grid place-items-center text-sm text-gray-600">A curva aparece após a primeira resolução.</div>
        <div v-else class="relative h-[190px]"><canvas ref="pnlChart"></canvas></div>
      </div>
      <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4 min-h-[260px]">
        <div class="mb-3">
          <h2 class="text-sm font-semibold text-white">Estados das entradas</h2>
          <p class="text-[11px] text-gray-600">Distribuição do laboratório atual</p>
        </div>
        <div v-if="!displayTrades.length" class="h-[190px] grid place-items-center text-sm text-gray-600">Sem entradas para comparar.</div>
        <div v-else class="relative h-[190px]"><canvas ref="statusChart"></canvas></div>
      </div>
    </section>

    <div class="grid lg:grid-cols-[1.2fr_0.8fr] gap-4">
      <section class="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 class="text-sm font-semibold text-white">Simulações</h2>
            <p class="text-[11px] text-gray-600">{{ trades.length + spotTrades.length + observations.length }} registros · {{ spotTrades.length }} entradas spot · {{ observations.length }} leituras</p>
          </div>
          <select v-model="filter" class="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300">
            <option value="">Todos</option>
            <option value="OPEN">Abertos</option>
            <option value="WIN">Wins</option>
            <option value="LOSS">Losses</option>
          </select>
        </div>
        <div v-if="message && !allTrades.length" class="p-6 text-center text-sm text-amber-400">{{ message }}<p class="text-xs text-gray-600 mt-2">O histórico Polymarket foi excluído desta tela por restrição geográfica.</p></div>
        <div v-else-if="filteredTrades.length === 0" class="p-10 text-center text-sm text-gray-600">Nenhuma simulação encontrada.</div>
        <div v-for="trade in filteredTrades" :key="trade.id" class="px-4 py-3 border-b border-gray-800/70 last:border-0">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm text-gray-200 truncate">{{ trade.question || trade.market_id || 'Mercado' }}</p>
              <p class="text-[11px] text-gray-600 mt-1">{{ trade.venue || 'venue desconhecida' }} · {{ trade.strategy || 'estratégia' }} · {{ formatDate(trade.opened_at) }}</p>
            </div>
            <span class="px-2 py-1 rounded-md text-[10px] font-bold" :class="statusClass(trade.result)">{{ trade.result }}</span>
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-gray-500">
            <span>Entrada {{ Number(trade.entry_price || 0).toFixed(3) }}</span>
            <span>Edge {{ Number(trade.edge_percent || 0).toFixed(2) }}%</span>
            <span>Stake ${{ Number(trade.stake || 0).toFixed(2) }}</span>
            <strong :class="Number(trade.pnl) >= 0 ? 'text-emerald-400' : 'text-red-400'">P&L {{ money(trade.pnl) }}</strong>
          </div>
        </div>
      </section>

      <section class="space-y-4">
        <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <h2 class="text-sm font-semibold text-white mb-3">Desempenho por venue</h2>
          <div v-if="!venueResults.length" class="text-sm text-gray-600">Ainda sem paper trades resolvidos.</div>
          <div v-for="item in venueResults" :key="item.venue" class="flex justify-between items-center py-2 border-b border-gray-800 last:border-0">
            <span class="text-xs text-gray-300">{{ item.venue }}</span>
            <span class="text-xs font-bold" :class="Number(item.pnl) >= 0 ? 'text-emerald-400' : 'text-red-400'">{{ money(item.pnl) }} · PF {{ item.profit_factor == null ? '-' : Number(item.profit_factor).toFixed(2) }}</span>
          </div>
        </div>
        <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <h2 class="text-sm font-semibold text-white mb-3">Desempenho por estratégia</h2>
          <div v-if="!summary.byStrategy.length" class="text-sm text-gray-600">Ainda sem trades resolvidos.</div>
          <div v-for="item in summary.byStrategy" :key="item.strategy" class="flex justify-between items-center py-2 border-b border-gray-800 last:border-0">
            <span class="text-xs text-gray-300">{{ item.strategy }}</span>
            <span class="text-xs font-bold" :class="Number(item.pnl) >= 0 ? 'text-emerald-400' : 'text-red-400'">{{ money(item.pnl) }} · {{ item.trades }} trades</span>
          </div>
        </div>
        <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <h2 class="text-sm font-semibold text-white mb-3">Resumo por operador</h2>
          <div v-if="!venueResults.length" class="text-sm text-gray-600">Sem entradas spot resolvidas ainda.</div>
          <div v-for="item in venueResults" :key="`venue-${item.venue}`" class="py-2 border-b border-gray-800 last:border-0">
            <div class="flex justify-between text-xs"><span class="text-gray-300">{{ item.venue }}</span><strong>{{ item.trades }} entradas</strong></div>
            <div class="text-[11px] text-gray-500 mt-1">{{ item.open || 0 }} abertas · {{ item.wins || 0 }} wins · {{ item.losses || 0 }} losses · P&L {{ money(item.pnl) }}</div>
          </div>
        </div>
        <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <h2 class="text-sm font-semibold text-white mb-3">Ganho potencial por ativo</h2>
          <div v-if="!spotBySymbol.length" class="text-sm text-gray-600">Aguardando entradas spot.</div>
          <div v-for="item in spotBySymbol" :key="`symbol-${item.symbol}`" class="py-2 border-b border-gray-800 last:border-0">
            <div class="flex justify-between text-xs"><span class="text-gray-300">{{ item.symbol }}</span><strong>{{ item.trades }} entradas</strong></div>
            <div class="text-[11px] mt-1"><span class="text-emerald-400">Alvo {{ money(item.potential_gain) }}</span><span class="text-red-400 ml-3">Stop {{ money(item.potential_loss) }}</span></div>
          </div>
        </div>
        <div class="bg-gray-900 border border-gray-800 rounded-2xl p-4">
          <h2 class="text-sm font-semibold text-white mb-3">Consenso e custos</h2>
          <div class="mb-3">
            <p class="text-[11px] text-gray-500">Taxa média</p>
            <p class="text-sm text-cyan-400">{{ (spotMetrics.costs?.avgFeeRate || 0).toFixed(4) }}% fee · {{ (spotMetrics.costs?.avgSlippageRate || 0).toFixed(4) }}% slip</p>
          </div>
          <div v-if="!spotMetrics.consensus?.bySymbol || !Object.keys(spotMetrics.consensus.bySymbol || {}).length" class="text-sm text-gray-600">Sem consenso suficiente.</div>
          <div v-else class="space-y-2">
            <div v-for="(metric, symbol) in spotMetrics.consensus.bySymbol" :key="symbol" class="rounded-lg border border-gray-800 p-2">
              <div class="flex justify-between text-[11px] text-gray-300"><span>{{ symbol }}</span><span>{{ metric.direction }} · {{ metric.agreement }}/{{ metric.totalVotes }}</span></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const trades = ref([]);
const filter = ref('');
const loading = ref(false);
const lastUpdate = ref(null);
const summary = ref({ pnl: 0, winRate: 0, profitFactor: null, maxDrawdown: 0, open: 0, byVenue: [], byStrategy: [] });
const message = ref('');
const error = ref('');
let timer = null;

const spotTrades = ref([]);
const observations = ref([]);
const spotMetrics = ref({ consensus: { bySymbol: {} }, costs: { avgFeeRate: 0, avgSlippageRate: 0 } });
const football = ref({ loading: true, error: '', net: null, hitRate: null, pending: 0, total: 0 });
const pnlChart = ref(null);
const statusChart = ref(null);
let pnlChartInstance = null;
let statusChartInstance = null;
const venueResults = computed(() => summary.value.byVenue?.length ? summary.value.byVenue : (summary.value.spotByVenue || []));
const spotBySymbol = computed(() => summary.value.spotBySymbol || []);
const allTrades = computed(() => [...trades.value, ...spotTrades.value]);
const displayTrades = computed(() => [...allTrades.value, ...observations.value.map(a => ({ ...a, id: `obs_${a.id}`, result: 'OBSERVATION', opened_at: a.timestamp, question: a.question, venue: a.venue, strategy: a.strategy, entry_price: a.market_probability, edge_percent: a.edge_percent, pnl: 0 }))]);
const filteredTrades = computed(() => filter.value ? displayTrades.value.filter(t => t.result === filter.value) : displayTrades.value);
const chartTrades = computed(() => [...allTrades.value]
  .filter(trade => ['WIN', 'LOSS'].includes(trade.result))
  .sort((a, b) => new Date(a.opened_at || 0) - new Date(b.opened_at || 0)));
const statCards = computed(() => [
  { label: 'P&L simulado', value: money(summary.value.pnl), class: summary.value.pnl >= 0 ? 'text-emerald-400' : 'text-red-400' },
  { label: 'Win rate', value: `${summary.value.winRate.toFixed(1)}%`, class: 'text-cyan-400' },
  { label: 'Profit factor', value: summary.value.profitFactor == null ? '-' : summary.value.profitFactor.toFixed(2), class: 'text-amber-400' },
  { label: 'Drawdown máximo', value: money(summary.value.maxDrawdown), class: 'text-red-400' },
  { label: 'Abertos', value: String(summary.value.open), class: 'text-white' },
]);
const money = (value) => `${Number(value || 0) >= 0 ? '+' : ''}$${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => value ? new Date(value).toLocaleString('pt-BR') : '-';
const statusClass = (status) => ({ OPEN: 'bg-amber-500/15 text-amber-400', WIN: 'bg-emerald-500/15 text-emerald-400', LOSS: 'bg-red-500/15 text-red-400' }[status] || 'bg-gray-800 text-gray-400');

function destroyCharts() {
  pnlChartInstance?.destroy();
  statusChartInstance?.destroy();
  pnlChartInstance = null;
  statusChartInstance = null;
}

function renderCharts() {
  destroyCharts();
  if (!pnlChart.value || !statusChart.value || !displayTrades.value.length) return;
  const gridColor = 'rgba(148, 163, 184, 0.12)';
  const tickColor = '#64748b';
  const cumulative = [];
  chartTrades.value.reduce((total, trade) => {
    const next = total + Number(trade.pnl || 0);
    cumulative.push(next);
    return next;
  }, 0);
  pnlChartInstance = new Chart(pnlChart.value, {
    type: 'line',
    data: {
      labels: chartTrades.value.map((trade, index) => formatDate(trade.opened_at).split(',')[0] || `#${index + 1}`),
      datasets: [{ data: cumulative, borderColor: '#22d3ee', backgroundColor: 'rgba(34, 211, 238, 0.10)', fill: true, tension: 0.35, pointRadius: 2, pointHoverRadius: 5 }],
    },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, ticks: { color: tickColor, maxTicksLimit: 6 } }, y: { grid: { color: gridColor }, ticks: { color: tickColor, callback: value => `$${Number(value).toFixed(2)}` } } } },
  });
  const states = ['WIN', 'LOSS', 'OPEN'];
  statusChartInstance = new Chart(statusChart.value, {
    type: 'doughnut',
    data: { labels: states, datasets: [{ data: states.map(state => displayTrades.value.filter(trade => trade.result === state).length), backgroundColor: ['#34d399', '#fb7185', '#fbbf24'], borderColor: '#111827', borderWidth: 4 }] },
    options: { responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { position: 'bottom', labels: { color: tickColor, boxWidth: 10, padding: 16, font: { size: 11 } } } } },
  });
}

async function loadFootball() {
  football.value.loading = true;
  football.value.error = '';
  try {
    const response = await fetch('/futebol-lab/live/data.json?t=' + Date.now(), { cache: 'no-store' });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const data = await response.json();
    const rows = data.datasets?.[0]?.rows || [];
    const settled = rows.filter(r => ['win', 'loss', 'push'].includes(r.status));
    const profit = (r) => r.status === 'push' ? 0 : r.status === 'win' ? (Number(r.odds) - 1) * 0.99 - 0.002 : -1.002;
    const net = settled.reduce((s, r) => s + profit(r), 0);
    const wins = settled.filter(r => r.status === 'win').length;
    const losses = settled.filter(r => r.status === 'loss').length;
    football.value = {
      loading: false, error: '', total: rows.length,
      net: settled.length ? net : null,
      hitRate: wins + losses ? wins / (wins + losses) : null,
      pending: rows.filter(r => r.status === 'pending').length,
    };
  } catch (e) {
    football.value = { ...football.value, loading: false, error: 'Futebol indisponível' };
  }
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const labUrl = process.env.VUE_APP_LAB_URL || 'https://apipoly.bitsis.com.br/lab/paper-trades';
    const response = await fetch(`${labUrl}?limit=300`, { cache: 'no-store' });
    if (!response.ok) {
      error.value = `Laboratório HTTP ${response.status}`;
      return;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      error.value = 'O laboratório retornou uma resposta inválida';
      return;
    }
    const data = await response.json();
    trades.value = data.trades || [];
    spotTrades.value = data.spotTrades || [];
    observations.value = data.observations || [];
    message.value = data.message || '';
    const raw = data.summary || {};
    const spotSummary = data.spotSummary || {};
    summary.value = {
      ...summary.value,
      ...raw,
      ...spotSummary,
      byVenue: spotSummary.byVenue || raw.byVenue || [],
      byStrategy: spotSummary.byStrategy || raw.byStrategy || [],
      spotByVenue: spotSummary.byVenue || [],
      spotBySymbol: spotSummary.bySymbol || [],
    };
    spotMetrics.value = {
      consensus: spotSummary.consensus || data.metrics?.consensus || { bySymbol: {} },
      costs: spotSummary.costs || data.metrics?.costs || { avgFeeRate: 0, avgSlippageRate: 0 },
    };
    lastUpdate.value = new Date();
    await nextTick();
    renderCharts();
  } catch (err) {
    error.value = 'Não foi possível carregar o laboratório';
    console.error('[SIMULACOES]', err);
  } finally {
    loading.value = false;
  }
}

onMounted(() => { load(); loadFootball(); timer = setInterval(() => { load(); loadFootball(); }, 30000); });
onUnmounted(() => { if (timer) clearInterval(timer); destroyCharts(); });
</script>

